import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { CheckCircleFill, XLg } from 'react-bootstrap-icons';
import { applyForEarlyAccess, getEarlyAccessSettings } from '../api/earlyAccess';
import {
    WORK_EMAIL_REQUIRED_MESSAGE,
    isNonWorkEmailAddress,
} from '../utils/emailValidation';
import './auth-shared.css';
import './EarlyAccess-new.css';

/* ===========================================================
   Early Access Page Component
   Public form that lets visitors request access to AcceliQA.
   Mirrors the styling of Register-new.tsx so the experience is
   consistent with the rest of the auth flow.
   =========================================================== */

interface EarlyAccessFormData
{
    full_name: string;
    email: string;
    company_name: string;
    notes: string;
}

interface FieldErrors
{
    full_name: string | null;
    email: string | null;
    company_name: string | null;
    notes: string | null;
}

function validateFields(
    formData: EarlyAccessFormData,
    workEmailError: string | null = null,
): FieldErrors
{
    const errors: FieldErrors = {
        full_name: null,
        email: null,
        company_name: null,
        notes: null,
    };

    if (!formData.full_name.trim())
    {
        errors.full_name = 'Full name is required';
    }
    if (!formData.email.trim())
    {
        errors.email = 'Email is required';
    }
    else if (!/\S+@\S+\.\S+/.test(formData.email))
    {
        errors.email = 'Please enter a valid email address';
    }
    else if (workEmailError)
    {
        errors.email = workEmailError;
    }
    if (!formData.company_name.trim())
    {
        errors.company_name = 'Company name is required';
    }
    if (!formData.notes.trim())
    {
        errors.notes = 'Reffered By is required';
    }

    return errors;
}

function isFormValid(errors: FieldErrors): boolean
{
    return Object.values(errors).every((val) => val === null);
}

const EarlyAccessNew: React.FC = () =>
{
    const navigate = useNavigate();

    const [formData, setFormData] = useState<EarlyAccessFormData>({
        full_name: '',
        email: '',
        company_name: '',
        notes: '',
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [workEmailError, setWorkEmailError] = useState<string | null>(null);
    const [companyEmailOnly, setCompanyEmailOnly] = useState<boolean>(true);

    useEffect(() =>
    {
        let mounted = true;
        getEarlyAccessSettings()
            .then((settings) =>
            {
                if (mounted)
                {
                    setCompanyEmailOnly(settings.company_email_only);
                }
            })
            .catch(() =>
            {
                // Fail closed in the browser. The backend remains authoritative.
                if (mounted)
                {
                    setCompanyEmailOnly(true);
                }
            });
        return () =>
        {
            mounted = false;
        };
    }, []);

    const fieldErrors: FieldErrors = validateFields(formData, workEmailError);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
    {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === 'email')
        {
            setWorkEmailError(null);
        }
        if (error)
        {
            setError(null);
        }
    }

    async function handleFormSubmit(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();
        setSubmitted(true);
        const errors = validateFields(formData, workEmailError);
        if (!isFormValid(errors))
        {
            return;
        }

        setLoading(true);
        setError(null);
        try
        {
            if (companyEmailOnly && await isNonWorkEmailAddress(formData.email))
            {
                setWorkEmailError(WORK_EMAIL_REQUIRED_MESSAGE);
                return;
            }
            await applyForEarlyAccess({
                full_name: formData.full_name.trim(),
                email: formData.email.trim(),
                company_name: formData.company_name.trim(),
                notes: formData.notes.trim(),
            });
            setSuccess(true);
        }
        catch (err: any)
        {
            const message =
                err.response?.data?.detail ||
                'We could not submit your request. Please try again.';
            setError(message);
        }
        finally
        {
            setLoading(false);
        }
    }

    function handleClose(): void
    {
        navigate('/');
    }

    function renderSuccess(): React.ReactElement
    {
        return (
            <div className="early-access-success">
                <div className="early-access-success-icon">
                    <CheckCircleFill size={48} />
                </div>
                <h2 className="auth-heading">Request received</h2>
                <p className="auth-subtitle">
                    Thanks! We'll review your application and send you a signup
                    link as soon as it's approved.
                </p>
                <button
                    type="button"
                    className="auth-submit-btn"
                    onClick={handleClose}
                >
                    Back to home
                </button>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card">
                <button
                    type="button"
                    className="auth-close-btn"
                    onClick={handleClose}
                    aria-label="Close early-access form"
                >
                    <XLg size={18} />
                </button>

                <div className="text-center">
                    <img
                        src="/Accelirate.svg"
                        alt="AcceliQA Logo"
                        className="auth-logo"
                    />
                    <h2 className="auth-heading">Request Early Access</h2>
                    <p className="auth-subtitle">
                        AcceliQA is currently invite-only. Tell us about yourself
                        and we'll be in touch.
                    </p>
                </div>

                {success ? renderSuccess() : (
                    <>
                        {error && (
                            <div className="auth-server-error">{error}</div>
                        )}
                        <Form onSubmit={handleFormSubmit} noValidate>
                            <div className="auth-form-group">
                                <Form.Label className="auth-label">Full Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your full name"
                                    required
                                    isInvalid={submitted && !!fieldErrors.full_name}
                                />
                                {submitted && fieldErrors.full_name && (
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.full_name}
                                    </Form.Control.Feedback>
                                )}
                            </div>

                            <div className="auth-form-group">
                                <Form.Label className="auth-label">
                                    {companyEmailOnly ? 'Work Email' : 'Email'}
                                </Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder={companyEmailOnly ? 'you@company.com' : 'you@example.com'}
                                    required
                                    isInvalid={submitted && !!fieldErrors.email}
                                />
                                {submitted && fieldErrors.email && (
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.email}
                                    </Form.Control.Feedback>
                                )}
                            </div>

                            <div className="auth-form-group">
                                <Form.Label className="auth-label">Company Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    placeholder="Acme Inc."
                                    required
                                    isInvalid={submitted && !!fieldErrors.company_name}
                                />
                                {submitted && fieldErrors.company_name && (
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.company_name}
                                    </Form.Control.Feedback>
                                )}
                            </div>

                            <div className="auth-form-group">
                                <Form.Label className="auth-label">Reffered By</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Enter the name of the person who referred you."
                                    rows={3}
                                    className="early-access-textarea"
                                    required
                                    isInvalid={submitted && !!fieldErrors.notes}
                                />
                                {submitted && fieldErrors.notes && (
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.notes}
                                    </Form.Control.Feedback>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="auth-submit-btn"
                                disabled={loading}
                            >
                                {loading
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : 'Request Access'
                                }
                            </button>

                            <p className="auth-footer-text">
                                Already have an account?
                                <Link to="/login" className="auth-footer-link">
                                    Sign In
                                </Link>
                            </p>
                        </Form>
                    </>
                )}
            </div>
        </div>
    );
};

export default EarlyAccessNew;
